import ApiConfig from '@config/api.config'
import { EVENT_KEYS } from '../constants/keys.contants'
import { getFromStorage } from '@helpers/storage.helpers'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

export class BaseApi {
  api: AxiosInstance
  download: AxiosInstance
  upload: AxiosInstance
  kvk: AxiosInstance
  requests: AxiosRequestConfig[]
  controller: AbortController

  addInterceptor = (instance: AxiosInstance, includeAuth = true): void => {
    instance.interceptors.request.use(request => {
      request.signal = this.controller.signal

      // Add token to request headers just before sending
      if (includeAuth) {
        const token = getFromStorage('access_token')
        if (token) {
          request.headers.Authorization = `Bearer ${token}`
        }
      }

      return request
    })

    instance.interceptors.response.use(
      response => {
        // Check for new token in Authorization header (axios normalizes to lowercase)
        const newToken = response.headers['authorization'] || response.headers['Authorization']

        if (newToken) {
          console.log('[API Interceptor] New token detected in response headers')
          // Strip 'Bearer ' prefix if present
          const token = newToken.startsWith('Bearer ') ? newToken.substring(7) : newToken

          const newTokenEvent = new CustomEvent(EVENT_KEYS.NEW_TOKEN, {
            detail: token,
          })
          console.log('[API Interceptor] Dispatching NEW_TOKEN event')
          window.dispatchEvent(newTokenEvent)
        }

        return response
      },
      error => {
        // Handle network errors and timeouts (no response object)
        if (!error.response) {
          return Promise.reject({
            message: error.message || 'Network error or timeout occurred',
            errors: {},
          })
        }

        const { status } = error.response
        // Example for handling internal error
        if (status === 500) {
          const serverError = new Event(EVENT_KEYS.SERVER_ERROR)
          window.dispatchEvent(serverError)
        }

        // AccessToken fails - but skip if it's an email verification error
        // Email verification errors return 403 but should be handled by the component
        const isEmailVerificationError = error.response.data?.message?.includes('verify your email') ||
                                          error.response.data?.message?.includes('e-mailadres verifiëren')
        if (status === 403 && !isEmailVerificationError) {
          const unauthEvent = new Event(EVENT_KEYS.UNAUTH)
          window.dispatchEvent(unauthEvent)
        }

        //RefreshToken fails
        if (status === 401) {
          const logoutEvent = new Event(EVENT_KEYS.LOGOUT)
          window.dispatchEvent(logoutEvent)
        }

        // Handle default error
        return Promise.reject(error.response.data)
      }
    )
  }

  constructor() {
    this.controller = new AbortController()
    const _api = axios.create(ApiConfig.api)
    this.addInterceptor(_api, true)
    this.api = _api

    this.kvk = axios.create(ApiConfig.kvk)
    this.addInterceptor(this.kvk, false) // KVK doesn't need auth

    this.download = axios.create(ApiConfig.download)
    this.addInterceptor(this.download, true)

    this.upload = axios.create(ApiConfig.upload)
    this.addInterceptor(this.upload, true)

    this.requests = []
  }
}
